import axios from 'axios';

const { REACT_APP_FLASK_API_URL } = process.env;

/**
 * List Yext locations with optional filters
 * @param {Object} params - Query parameters
 * @param {string} [params.name] - Filter by location name
 * @param {string} [params.address] - Filter by address
 * @param {string} [params.phone] - Filter by phone
 * @param {string} [params.status] - Filter by status
 * @param {string} [params.ordering] - Field to order by
 * @param {number} [params.page] - Page number
 * @param {number} [params.page_size] - Results per page
 * @returns {Promise<import('axios').AxiosResponse>} List of Yext locations
 */
export const yextList = async (params = {}) => {
  const response = await axios
    .get(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/`, { params });
  
  return response;
};

/**
 * Create a new Yext location
 * @param {Object} locationData - Location data
 * @returns {Promise<import('axios').AxiosResponse>} Created location
 */
export const yextCreate = async (locationData) => {
  const response = await axios
    .post(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/`, locationData);
  
  return response;
};

/**
 * Retrieve a specific Yext location
 * @param {string} id - Location ID
 * @returns {Promise<import('axios').AxiosResponse>} Location data
 */
export const yextRetrieve = async (id) => {
  const response = await axios
    .get(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/${id}/`);
  
  return response;
};

/**
 * Update a Yext location
 * @param {string} id - Location ID
 * @param {Object} locationData - Updated location data
 * @returns {Promise<import('axios').AxiosResponse>} Updated location
 */
export const yextUpdate = async (id, locationData) => {
  const response = await axios
    .put(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/${id}/`, locationData);
  
  return response;
};

/**
 * Partially update a Yext location
 * @param {string} id - Location ID
 * @param {Object} locationData - Partial location data
 * @returns {Promise<import('axios').AxiosResponse>} Updated location
 */
export const yextPartialUpdate = async (id, locationData) => {
  const response = await axios
    .patch(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/${id}/`, locationData);
  
  return response;
};

/**
 * Delete a Yext location
 * @param {string} id - Location ID
 * @returns {Promise<import('axios').AxiosResponse>} Deletion confirmation
 */
export const yextDestroy = async (id) => {
  const response = await axios
    .delete(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/${id}/`);
  
  return response;
};

/**
 * Get proxy report for a Yext location (scan results)
 * Returns HTML content to be embedded in an iframe
 * @param {string} id - Location ID
 * @returns {Promise<import('axios').AxiosResponse>} HTML scan results
 */
export const yextProxyReport = async (id) => {
  const response = await axios
    .get(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/${id}/proxy-report/`, {
      responseType: 'text', // Get HTML as text instead of parsing as JSON
      headers: {
        'Accept': 'text/html',
      },
    });
  
  return response;
};

/**
 * Get Google reviews directly from Google via SERPAPI
 * Returns accurate 'needs reply' count based on actual review responses
 * @param {string} id - Location ID (nap_listing UUID)
 * @param {Object} params - Query parameters
 * @param {string} [params.sort_by] - Sort order (newestFirst, mostRelevant, etc)
 * @param {string} [params.next_page_token] - Token for pagination
 * @returns {Promise<import('axios').AxiosResponse>} Google reviews data
 */
export const yextGoogleReviews = async (id, params = {}) => {
  // Use the existing review-removals endpoint which fetches from SERPAPI
  const response = await axios
    .get(`${REACT_APP_FLASK_API_URL}/dash2-proxy/review-removals/google-reviews/`, {
      params: { nap_listing: id, ...params },
    });

  return response;
};

/**
 * Import CSV file for Yext locations
 * @param {File} file - CSV file to import
 * @returns {Promise<import('axios').AxiosResponse>} Import results
 */
export const yextImportCsv = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios
    .post(`${REACT_APP_FLASK_API_URL}/dash2-proxy/yext/import-csv/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  
  return response;
};

// Default export for convenience
const yextService = {
  list: yextList,
  create: yextCreate,
  retrieve: yextRetrieve,
  update: yextUpdate,
  partialUpdate: yextPartialUpdate,
  destroy: yextDestroy,
  proxyReport: yextProxyReport,
  importCsv: yextImportCsv,
  googleReviews: yextGoogleReviews,
};

export default yextService;
